import React, { useEffect, useRef, useState, useLayoutEffect } from 'react';
import Globe from 'react-globe.gl';
import {
  Text, Card, CardHeader, Box, Button, CheckBox, Toolbar, Tip, Image, Layer
} from 'grommet';
import { Expand, Contract } from 'grommet-icons';
import { usePersistentState } from '../../utils/UsePersistentState';
import countryData from './country-orders.json'; // 

const colorMap = {
  green: '#01a982',
  yellow: '#fec901',
  red: '#c54e4b',
};

const transformCountryData = (data) => {
  return data.map((entry, i) => {
    let status;
    if (entry.ordersOnHold === 0) status = 'green';
    else if (entry.ordersOnHold <= 15) status = 'yellow';
    else status = 'red';

    return {
      id: i,
      lat: entry.lat,
      lng: entry.lng,
      name: entry.country,
      country: entry.country,
      status,
      ordersOnHold: entry.ordersOnHold,
    };
  });
};

export const HomeGlobe3D = () => {
  const globeRef = useRef();
  const [sites, setSites] = useState([]);
  const [fullscreen, setFullscreen] = useState(false);
  const [globeKey, setGlobeKey] = useState(0);
  const [hoveredPoint, setHoveredPoint] = useState(null);

  const [autoRotateFullscreen, setAutoRotateFullscreen] = usePersistentState('autoRotateFullscreen', true);
  const [autoRotatePreview, setAutoRotatePreview] = usePersistentState('autoRotatePreview', true);

  const currentAutoRotate = fullscreen ? autoRotateFullscreen : autoRotatePreview;

  useEffect(() => {
    const transformed = transformCountryData(countryData);
    setSites(transformed);
  }, []);

  useLayoutEffect(() => {
    let animationFrameId;

    const applyAutoRotate = () => {
      const controls = globeRef.current?.controls?.();
      if (controls) {
        controls.autoRotate = currentAutoRotate;
        controls.autoRotateSpeed = 0.3;
      } else {
        animationFrameId = requestAnimationFrame(applyAutoRotate);
      }
    };

    applyAutoRotate();

    return () => cancelAnimationFrame(animationFrameId);
  }, [fullscreen, globeKey, currentAutoRotate]);

  const GlobeComponent = (
    <Globe
      key={globeKey}
      ref={globeRef}
      globeImageUrl="//cdn.jsdelivr.net/npm/three-globe/example/img/earth-night.jpg"
      backgroundImageUrl="//cdn.jsdelivr.net/npm/three-globe/example/img/night-sky.png"
      pointsData={sites}
      pointLat="lat"
      pointLng="lng"
      pointAltitude={0.01}
      pointRadius={(d) =>
        d.ordersOnHold === 0
          ? 0.5 // make green dots bigger
          : Math.min(0.2 + d.ordersOnHold / 15, 1)
      }
      pointColor={(d) => colorMap[d.status]}
      onPointHover={setHoveredPoint}
    />
  );

  const TooltipCard = hoveredPoint && (
    <Card
      elevation="medium"
      round="small"
      style={{
        position: 'absolute',
        bottom: '40%',
        right: '20px',
        zIndex: 1000,
        maxWidth: '300px',
      }}
    >
      <CardHeader pad="small" background="brand">
        <Text size="medium" weight="bold">{hoveredPoint.name}</Text>
      </CardHeader>
      <Box pad="medium">
        <Text>
          Orders on hold:{' '}
          <strong>
            {hoveredPoint.ordersOnHold}
          </strong>
        </Text>
      </Box>
    </Card>
  );

  return (
    <>
      {!fullscreen && (
        <Card elevation="medium" round="small" margin={{ top: 'none' }} style={{ position: 'relative' }}>
          <CardHeader
            pad="small"
            background="background"
            direction="row"
            justify="between"
            align="center"
          >
            <Text size="medium" weight="bold">Global Holds View</Text>
            <Toolbar>
              <Box direction="row" align="center" gap="small">
                <Tip content="Auto rotate globe (on/off)..." dropProps={{ align: { top: 'bottom' }, margin: { top: 'medium' }, }}>
                  <CheckBox
                    toggle
                    checked={autoRotatePreview}
                    onChange={(event) => setAutoRotatePreview(event.target.checked)}
                  />
                </Tip>
                <Button
                  icon={<Expand />}
                  onClick={() => {
                    setFullscreen(true);
                    setGlobeKey(prev => prev + 1);
                  }}
                  plain
                />
              </Box>
            </Toolbar>
          </CardHeader>

          <Box align="center" justify="center">
            {GlobeComponent}
          </Box>

          {TooltipCard}
        </Card>
      )}

      {fullscreen && (
        <Layer
          full
          modal
          onEsc={() => setFullscreen(false)}
          onClickOutside={() => setFullscreen(false)}
          plain
        >
          <Box fill background="black" style={{ position: 'relative' }}>
            <Box
              direction="row"
              align="center"
              gap="small"
              style={{ position: 'absolute', top: '16px', left: '16px', zIndex: 20 }}
            >
              <Image src="/images/hpe-element.png" width="50px" />
              <Text size="large" weight="bold" color="white">GT Console</Text>
            </Box>

            <Box style={{ position: 'absolute', top: '16px', right: '16px', zIndex: 20 }}>
              <Toolbar>
                <Box direction="row" align="center" gap="medium">
                  <Tip content="Auto rotate globe (fullscreen)..." dropProps={{ align: { top: 'bottom' } }}>
                    <CheckBox
                      toggle
                      checked={autoRotateFullscreen}
                      onChange={(event) => setAutoRotateFullscreen(event.target.checked)}
                    />
                  </Tip>
                  <Button
                    icon={<Contract />}
                    onClick={() => setFullscreen(false)}
                    plain
                  />
                </Box>
              </Toolbar>
            </Box>

            <Box fill align="center" justify="center">
              {GlobeComponent}
              {TooltipCard}
            </Box>
          </Box>
        </Layer>
      )}
    </>
  );
};


